import { CheckIcon, DeviceMobileIcon } from '@phosphor-icons/react'
import type { Device } from '@robotensor/api'
import { Popover, toast } from '@robotensor/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useEffect, useState, type ReactElement } from 'react'
import { robot, sim } from '../lib/api.ts'
import { phoneFor, phoneLink, phoneName, phonesOf, pillState, pillText, quality, usePhoneLink, type PillState } from '../lib/phoneLink.ts'
import { queries } from '../lib/queries.ts'
import { useControlModes } from '../sim/modes.ts'
import { Chip, GetTheApp } from './parts.tsx'

const CHIP: Record<PillState, { text: string; tone: 'ok' | 'chk' | 'new' }> = {
  none: { text: 'No phone', tone: 'chk' },
  ready: { text: 'Ready', tone: 'ok' },
  calling: { text: 'Calling', tone: 'new' },
  linked: { text: 'Linked', tone: 'ok' },
  driving: { text: 'Driving', tone: 'new' },
}

/** "AR (Wand)" on phones with AR, "Touch (Gamepad)" on the others (controller-and-review.html, D1/D2). */
export function controllerOf(d: Device | undefined): string {
  return d?.capabilities && (d.capabilities as Record<string, unknown>).ar_pose ? 'AR (Wand)' : 'Touch (Gamepad)'
}

/**
 * The phone link pill in the bar (W10): No phone · Pixel 8 ready · Calling · 47 · Linked · Driving, and its menu with
 * link quality, controller, when the link ends, Use another phone and End link.
 */
export function PhonePill(): ReactElement {
  const devices = useQuery(queries.devices())
  const { modes } = useControlModes()
  const calls = modes.has('phone_call')
  const link = usePhoneLink()
  const phones = phonesOf(devices.data?.devices)
  const phone = phoneFor(devices.data?.devices, link.pickedDeviceId)
  const state = pillState(link, phone)
  const [open, setOpen] = useState(false)
  const [choosing, setChoosing] = useState(false)

  // the standby link lives on the sim service: once it ends there (phone left, 10 idle minutes), the pill says so
  const standby = useQuery({
    queryKey: ['sim', 'pairing', link.linked?.pairingId],
    queryFn: () => sim.pairings.get(link.linked!.pairingId),
    enabled: !!link.linked && !link.driving,
    refetchInterval: 20_000,
    retry: false,
  })
  useEffect(() => {
    if (!link.linked || link.driving) return
    if (standby.isError || standby.data?.state === 'expired') phoneLink.unlink()
  }, [standby.isError, standby.data?.state, link.linked, link.driving])

  const endLink = async () => {
    const id = link.linked?.pairingId
    phoneLink.unlink()
    setOpen(false)
    if (id) await robot.standby.end(id).catch(() => undefined)
    toast({ title: 'Link ended', description: `${phoneName(phone)} is no longer linked to this computer.` })
  }

  const since = link.linked ? new Date(link.linked.since).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''
  const name = link.linked?.device ?? phoneName(phone)
  const q = quality(link.latencyMs)
  const text =
    state === 'linked'
      ? `Linked to this computer since ${since}. Your next task goes straight to the phone.`
      : state === 'driving'
        ? 'Driving now: the robot follows this phone.'
        : state === 'calling'
          ? 'Calling now. Open the alert on your phone and tap the number on this screen.'
          : calls && phone?.answer_calls
            ? 'When you press Start, this phone rings.'
            : calls
              ? `Calls are off on ${phoneName(phone)}. Turn on Answer my computer in Account › Devices.`
              : 'When you press Start, scan the code on this screen with this phone.'

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) setChoosing(false)
      }}
      align="end"
      trigger={
        <button type="button" className="w-pill" data-state={state} aria-label={`Phone: ${pillText(state, link, phone)}`}>
          {state === 'none' ? <DeviceMobileIcon weight="bold" aria-hidden /> : <span className="w-pill__dot" aria-hidden />}
          <span className="w-pill__text">{pillText(state, link, phone)}</span>
        </button>
      }
    >
      <div className="w-pop w-phonemenu">
        {state === 'none' ? (
          <GetTheApp size={112} />
        ) : choosing ? (
          <>
            <b>Which phone gets your next task?</b>
            <div className="w-list w-list--sm" role="radiogroup" aria-label="Phones">
              {phones.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  role="radio"
                  aria-checked={d.id === phone?.id}
                  className="w-choice"
                  onClick={() => {
                    phoneLink.pick(d.id)
                    setChoosing(false)
                  }}
                >
                  <DeviceMobileIcon aria-hidden />
                  <span className="w-grow">{phoneName(d)}</span>
                  {d.id === phone?.id ? <CheckIcon weight="bold" aria-hidden /> : null}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="w-row">
              <DeviceMobileIcon aria-hidden />
              <b className="w-grow">{name}</b>
              <Chip tone={CHIP[state].tone}>{CHIP[state].text}</Chip>
            </div>
            <p className="w-pop__text">{text}</p>
            <div className="w-list w-list--sm">
              {state === 'linked' || state === 'driving' ? (
                <div>
                  <span className="w-grow">Link quality</span>
                  <b>{q.text}</b>
                </div>
              ) : null}
              <div>
                <span className="w-grow">Controller</span>
                <b>{controllerOf(phone)}</b>
              </div>
              {state === 'linked' ? (
                <div>
                  <span className="w-grow">Ends after</span>
                  <b>10 min idle</b>
                </div>
              ) : null}
            </div>
            <div className="w-row w-row--wrap">
              {phones.length > 1 ? (
                <button type="button" className="w-btn" data-v="ghost" data-size="sm" onClick={() => setChoosing(true)}>
                  Use another phone
                </button>
              ) : (
                <Link to="/account/$tab" params={{ tab: 'devices' }} className="w-btn" data-v="ghost" data-size="sm" onClick={() => setOpen(false)}>
                  Use another phone
                </Link>
              )}
              {state === 'linked' ? (
                <button type="button" className="w-btn" data-v="dark" data-size="sm" onClick={() => void endLink()}>
                  End link
                </button>
              ) : null}
            </div>
          </>
        )}
      </div>
    </Popover>
  )
}
